/*
 * BulkImportManager selectors
 */

import { createSelector } from '@reduxjs/toolkit';
import { REDUCER_KEY } from './constants';

const selectBulkImportDomain = state => state[REDUCER_KEY] || {};

// File Upload Selectors
export const makeSelectUploadLoadingState = () =>
  createSelector(
    selectBulkImportDomain,
    substate => substate.uploadLoadingState,
  );

export const makeSelectUploadProgress = () =>
  createSelector(selectBulkImportDomain, substate => substate.uploadProgress);

export const makeSelectUploadedFile = () =>
  createSelector(selectBulkImportDomain, substate => substate.uploadedFile);

export const makeSelectFileId = () =>
  createSelector(selectBulkImportDomain, substate => substate.fileId);

// File Analysis Selectors
export const makeSelectAnalysisLoadingState = () =>
  createSelector(
    selectBulkImportDomain,
    substate => substate.analysisLoadingState,
  );

export const makeSelectAnalysisResult = () =>
  createSelector(selectBulkImportDomain, substate => substate.analysisResult);

// Required Fields Selectors
export const makeSelectRequiredFieldsLoadingState = () =>
  createSelector(
    selectBulkImportDomain,
    substate => substate.requiredFieldsLoadingState,
  );

export const makeSelectRequiredFields = () =>
  createSelector(selectBulkImportDomain, substate => substate.requiredFields);

// Column Mapping Selectors
export const makeSelectColumnMapping = () =>
  createSelector(selectBulkImportDomain, substate => substate.columnMapping);

export const makeSelectIsMappingComplete = () =>
  createSelector(
    [makeSelectColumnMapping(), makeSelectRequiredFields()],
    (columnMapping, requiredFields) => {
      if (!requiredFields || requiredFields.length === 0) return false;

      return requiredFields.every(
        field => columnMapping[field] && columnMapping[field].trim() !== '',
      );
    },
  );

// Import Job Selectors
export const makeSelectImportJobLoadingState = () =>
  createSelector(
    selectBulkImportDomain,
    substate => substate.importJobLoadingState,
  );

export const makeSelectCurrentJob = () =>
  createSelector(selectBulkImportDomain, substate => substate.currentJob);

export const makeSelectJobProgress = () =>
  createSelector(selectBulkImportDomain, substate => substate.jobProgress);

export const makeSelectJobStatus = () =>
  createSelector(
    selectBulkImportDomain,
    substate => substate.jobProgress?.status,
  );

export const makeSelectIsJobInProgress = () =>
  createSelector(
    makeSelectJobStatus(),
    status => status === 'in_progress' || status === 'pending',
  );

// WebSocket Selectors
export const makeSelectIsWebSocketConnected = () =>
  createSelector(selectBulkImportDomain, substate => substate.isConnected);

// UI State Selectors
export const makeSelectCurrentStep = () =>
  createSelector(selectBulkImportDomain, substate => substate.currentStep);

// Computed Selectors
export const makeSelectCanProceedToMapping = () =>
  createSelector(
    [makeSelectAnalysisResult(), makeSelectRequiredFields()],
    (analysisResult, requiredFields) =>
      Boolean(analysisResult && requiredFields && requiredFields.length > 0),
  );

export const makeSelectCanStartImport = () =>
  createSelector(
    [makeSelectIsMappingComplete(), makeSelectFileId(), makeSelectJobStatus()],
    (isMappingComplete, fileId, jobStatus) =>
      Boolean(isMappingComplete && fileId && jobStatus === 'idle'),
  );

export const makeSelectProgressPercentage = () =>
  createSelector(makeSelectJobProgress(), progress => {
    if (!progress || progress.totalRows === 0) return 0;
    return Math.round((progress.processedRows / progress.totalRows) * 100);
  });

export const makeSelectHasErrors = () =>
  createSelector(makeSelectJobProgress(), progress =>
    Boolean(progress && progress.errors && progress.errors.length > 0),
  );

export const makeSelectShouldShowPageLeaveWarning = () =>
  createSelector(
    [
      makeSelectUploadLoadingState(),
      makeSelectAnalysisLoadingState(),
      makeSelectImportJobLoadingState(),
      makeSelectJobStatus(),
    ],
    (uploadState, analysisState, importJobState, jobStatus) =>
      uploadState === 1 || // uploading
      analysisState === 1 || // analyzing
      importJobState === 1 || // creating job
      jobStatus === 'in_progress' ||
      jobStatus === 'pending',
  );

// Export domain selector for use in sagas
export const selectBulkImport = selectBulkImportDomain;
